<template>
  <div>
    <div v-for="AIValue in AIInteractions" :key="AIValue._id" style="position:relative">
      <div class="input-interaction-wrapper" :class="{ expanded: showFullInput.includes(AIValue._id)}">
        <span v-if="!showFullInput.includes(AIValue._id)" class="truncated-text-wrapper">
          <template v-if="isInputTextTruncated(AIValue.input_text)">
            <span v-html="getTruncatedText(AIValue.input_text)"></span>
            <span class="load-more" @click="showFullInput.push(AIValue._id)">{{$t('... See more')}}</span>
          </template>
          <template v-else>
            <span v-html="AIValue.input_text"></span>
          </template>
        </span>
      
        <span v-else>
          <span v-html="AIValue.input_text"></span>
          <span class="load-more" @click="removeFromList(AIValue._id)"> {{$t('See less')}}</span>
        </span>
      </div>
      <div style="width: 100%;display: flex;justify-content: end;">
        <div class="button-name-display">{{AIValue.button_name}}</div>
      </div>
    
      <div ref="outputPanel" class="output-interaction-wrapper">
        <span v-if="!AIValue.isThinking && !AIValue.isTyping" id="is_thinking_text">{{$t('AI Response')}}</span>
        
        <div v-if="AIValue.isThinking" class="ai-thinking" id="aiThinking">
          <span class="shimmer">{{$t('AI is crafting your response...')}}</span>
        </div>
        
        <div v-else-if="AIValue.isTyping" v-html="AIValue.displayedText"></div>
        
        <div v-else v-html="AIValue.formatted_text"></div>
      </div>
    
      <div v-if="AIValue._id === activeInteraction?._id && !AIValue.isThinking && !AIValue.isTyping && (AIValue.formatted_text && AIValue.formatted_text.length) && showInsertButton" class="interaction-buttons d-flex align-items-center gap-3">
        <button 
          type="button"
          class="btn btn-outline-secondary interaction-btn insert-btn"
          @click="handleInsertClick(AIValue)"
        >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#b6b6b6" class="bi bi-arrow-up-left-square" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm10.096 8.803a.5.5 0 1 0 .707-.707L6.707 6h2.768a.5.5 0 1 0 0-1H5.5a.5.5 0 0 0-.5.5v3.975a.5.5 0 0 0 1 0V6.707z"/>
        </svg>
          Insert
        </button>
      
        <button type="button" title="Copy"  class="btn btn-outline-secondary interaction-btn copy-btn" style="width:36px" @click="copyToClipboard(AIValue)">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#b6b6b6" class="bi bi-files mr-1" viewBox="0 0 16 16">
            <path d="M13 0H6a2 2 0 0 0-2 2 2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2 2 2 0 0 0 2-2V2a2 2 0 0 0-2-2m0 13V4a2 2 0 0 0-2-2H5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1M3 4a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/>
          </svg>
        </button>
      </div>
      
      <div class="scroll-bottom-btn" v-if="showScrollDownBtn && !AIValue.isTyping && AIValue._id === activeInteraction?._id">
        <button type="button" title="Scroll to bottom"  class="btn btn-outline-secondary" @click="scrollToBottom">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-arrow-down" viewBox="0 0 16 16">
            <path fill-rule="evenodd" d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>
  
<script>
import Constants from "../../components.constants.js"
import {mapMutations} from "vuex";

export default {
  name: "AIInteraction",
  props: {
    AIInteractions: {
      type: Array,
      required: true,
    },
    showInsertButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showFullInput: [],
      showScrollDownBtn: false,
      highlighted:null
    };
  },
  computed: {
    // Identifies the last, and therefore active, interaction
    activeInteraction() {
      return this.AIInteractions.length > 0
        ? this.AIInteractions[this.AIInteractions.length - 1]
        : null;
    }
  },
  watch: {
    AIInteractions: {
      deep: true, // Crucial for watching changes inside the interaction objects
      handler(newVal, oldVal) {
        if (newVal.length > oldVal.length) {
          const newInteraction = this.activeInteraction;
          if (newInteraction) {
            this.$set(newInteraction, 'isThinking', false);
            this.$set(newInteraction, 'isTyping', false);
            this.$set(newInteraction, 'displayedText', '');
            
            this.startProgressAndStreaming(newInteraction);
          }
        }
      },
    },
    showInsertButton(newVal) {
      if (newVal) {
        this.startTyping(this.AIInteractions[this.AIInteractions.length - 1]);
        setTimeout(() => {
          this.highlighted = this.AIInteractions[this.AIInteractions.length - 1];
        }, 2000);
        this.highlighted = null;
      }
    }
  },
  methods: {
    ...mapMutations("websocket",["addWSEventListener", "removeWSEventListener"]),
    updateProgress(notification){
      notification = notification.detail && Object.keys(notification.detail).length!=0 ? notification.detail : notification
      let job_info = notification.message
      
      let interaction = this.AIInteractions.find(job_ => job_.job_id === job_info.job_id);
      
      if (interaction) {
          Object.assign(interaction, job_info);
          
          const isFinished = job_info.state === 'COMPLETED' || job_info.state === 'FAILED';

          if (!isFinished) {
              this.$set(interaction, 'isThinking', true);
              this.$set(interaction, 'isTyping', false);
          } else {
              this.$set(interaction, 'isThinking', false);
          }

      }
    },
    isInputTextTruncated(text) {
      return text?.split(/\r\n|\r|\n/).length > 3;
    },
    getTruncatedText(text) {
      const lines = text?.split(/\r\n|\r|\n/);
      return lines?.slice(0, 3).join("\n");
    },
    
    removeFromList(id){
      this.showFullInput = this.showFullInput.filter(item => item !== id);
    },

    handleScroll() {
      const scrollPanel = this.$parent.$refs.scrollPanel;
      if (!scrollPanel) return;
      this.checkScrollState(scrollPanel);
    },

    checkScrollState(scrollPanel) {
      const scrollHeight = scrollPanel.scrollHeight;
      const clientHeight = scrollPanel.clientHeight;
      const scrollTop = scrollPanel.scrollTop;

      const hasScrollbar = scrollHeight > clientHeight;
      const notAtBottom = scrollTop + clientHeight < scrollHeight - 10;

      this.showScrollDownBtn = hasScrollbar && notAtBottom;
    },

    scrollToBottom() {
      const scrollPanel = this.$parent.$refs.scrollPanel;
      if (scrollPanel) {
        scrollPanel.scrollTo({ top: scrollPanel.scrollHeight, behavior: "smooth" });
      }
    },
    
    startProgressAndStreaming(interaction) {
      if (!interaction) return; 

      if (interaction.job_id) {
          this.$set(interaction, 'isThinking', true);
          this.$set(interaction, 'isTyping', false);
      } else {
          this.$set(interaction, 'isThinking', true);
          this.$set(interaction, 'isTyping', false);

          setTimeout(() => {
            this.$set(interaction, 'isThinking', false);
            this.$set(interaction, 'isTyping', true);
            this.startTyping(interaction);
          }, 3000);
      }
    },

    startTyping(interaction) {
      if (!interaction) return;

      const textToType = interaction.formatted_text;
      const words = textToType.split(/(\s+)/).filter(s => s.length > 0); 
      let index = 0;

      this.$set(interaction, 'isTyping', true);
      this.$set(interaction, 'displayedText', ''); 

      const typeNextWord = () => {
        if (index < words.length) {
          const newText = interaction.displayedText + words[index];
          this.$set(interaction, 'displayedText', newText);
          
          index++;

          this.$nextTick(() => {
            const scrollPanel = this.$parent.$refs.scrollPanel;
            if (scrollPanel) {
                this.checkScrollState(scrollPanel); 
                if (scrollPanel.scrollTop + scrollPanel.clientHeight >= scrollPanel.scrollHeight - 50) {
                    scrollPanel.scrollTo({ top: scrollPanel.scrollHeight, behavior: "auto" });
                }
            }
          });

          // Adjust the delay for smoother typing, maybe longer for spaces/newlines
          let randomDelay = 40 + Math.random() * 40;
          if (words[index - 1].match(/\s/)) { // If the last appended segment was whitespace
              randomDelay = 10; // Shorten the delay for quick space insertion
          }
          
          // 3. Schedule the next word
          setTimeout(typeNextWord, randomDelay);
        } else {
          // 4. Typing is complete
          this.$set(interaction, 'isTyping', false);
        }
      };

      typeNextWord();
    },

    
    handleInsertClick(AIValue) {
      this.$emit("insert-click", AIValue);
    },
    async copyHTML(html) {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          // eslint-disable-next-line no-undef
          new ClipboardItem({ 
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([html], { type: "text/plain" })
          })
        ]);
      } else {
        // Fallback (old browsers)
        const el = document.createElement("textarea");
        el.value = html;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
    },

    async copyToClipboard(AIValue) {
      this.copyHTML(AIValue.formatted_text);
      //navigator.clipboard.writeText(AIValue.formatted_text);
    },
  },
  mounted() {
    const scrollPanel = this.$parent.$refs.scrollPanel;
    if (scrollPanel) {
      scrollPanel.addEventListener("scroll", this.handleScroll);
      this.checkScrollState(scrollPanel);
    }
    if (this.activeInteraction) {
      this.startProgressAndStreaming();
    }
    this.addWSEventListener({eventname:Constants.jobStatusUpdateNotification, callback:this.updateProgress, parent:"AI Interaction"});
  },
  beforeDestroy(){
      this.removeWSEventListener({eventname:Constants.jobStatusUpdateNotification, callback:this.updateProgress, parent:"AI Interaction"});   
  },
  beforeUnmount() {
    const scrollPanel = this.$parent.$refs.scrollPanel;
    if (scrollPanel) {
      scrollPanel.removeEventListener("scroll", this.handleScroll);
    }
  }
};
</script>

<style lang="scss" scoped>

    $border-color: 1px solid #4a4a4a;
    $border-color-only:#4a4a4a;
    $primary-background: #1a1e21;
    $secondary-background-color:#15181B;
    $bg-color-on-hover: #22272b;
    $text-color: #eee;
    $dull-text-color: #757575;
    $secondary-text-color: #9c9c9c;
    $active-blue : #007bff;
    $input-field-and-label-font-size:14px;
    $input-field-height:38px;
    $none-text-color:#4f4f4f;
    $input-field-padding:.375rem 10px;
    $scrollbar-thumb-background:#333;
    $scrollbar-track-background:#1a1e21;
    $scrollbar-thumb-on-hover-background:#555;
    $tags-background-color:#282d31;
    $tags-border-color:#363636;
    $panel-heading-font-size:18px;
    $secondary-border-color: 1px solid #2b2b2b;

    .scroll-bottom-btn{
      position:sticky;
      border-radius: 20px;
      margin: auto;
      bottom: -3px;
      width: 100%;
      display: flex;
      justify-content: center;
      button{
        padding: 5px;
        width: 35px;
        border-radius: 20px;
        display: flex;
        justify-content: center;
        background: $secondary-background-color;
        opacity: 0.8;
      }
    }

    .scroll-bottom-btn:hover {
      button{
        opacity: 1;
        background: $bg-color-on-hover;
      }
      svg{
        fill: $text-color;
        color: $text-color;
      }
    }

    .progress-container {
      position: relative;
      width: 100%;
      height: 24px;
      background-color: #2b2b2b;
      border-radius: 12px;
      overflow: hidden;
      margin-bottom: 8px;
    }
    
    .progress-bar {
      height: 100%;
      background-color: #007bff;
      width: 0%;
      transition: width 0.03s linear;
    }
    
    .progress-text {
      position: absolute;
      width: 100%;
      text-align: center;
      line-height: 24px;
      color: #eee;
      font-size: 14px;
    }

    .AI-interaction-wrapper{
        padding: 12px;
        height: 89% !important;
        overflow: auto !important;
        .input-interaction-wrapper{
          margin-inline-start: 26px;
          text-align: start;
          margin-bottom: 14px;
          white-space: pre-wrap;
          word-break: break-word;
          border: 1px solid #3a3a3a;
          border-radius: 15px;
          padding: 9px;
          background: #1e2022;
          user-select: text;
          cursor: auto;;
        }
        .output-interaction-wrapper{
          text-align: start;
          white-space: pre-wrap;
          word-break: break-word;
          border-radius: 15px;
          padding: 11px;
          user-select: text;
          cursor: auto;
          height: calc(100% - 53px);
          overflow: auto;
          padding-bottom: 0px;
          display: flex;
          flex-direction: column;  
          margin-bottom: 44px;
          span{
            color: $secondary-text-color;
            margin-bottom: 7px;
          }        
        }
        .interaction-buttons{
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: -31px;
          margin-inline-start:10px;
          span:hover{
            text-decoration: underline;
          }
        }
      }
      
      .truncated-text-wrapper {
        white-space: pre-wrap; // preserve line breaks
      }
      
      .load-more {
        display: inline;       // important: inline so it stays next to text
        margin-inline-start: 4px;      // optional spacing
        cursor: pointer;
        color: $secondary-text-color;
        font-weight: 500;
        &:hover {
          opacity: 0.8;
        }
      }

      .interaction-btn {
        display: flex;
        align-items: center;
        cursor: pointer;
        font-size: 14px;
        padding: 6px;
        border-radius: 4px;
        transition: background 0.2s;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap:4px;
      }

      .ai-thinking {
        display: flex;
        align-items: center;
        gap: 15px;
        padding: 4px;
        border-radius: 10px;
        margin-bottom: 10px;
        justify-content: flex-start;
        span {
          color: $secondary-text-color;
          font-size: 14px;
        }
      }

      .typing-dots {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
    }

    .shimmer {
        background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.3) 0%,
            rgba(255, 255, 255, 0.8) 50%,
            rgba(255, 255, 255, 0.3) 100%
        );
        background-size: 200% 100%;
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        animation: shimmer 4s ease-in-out infinite;
        font-weight: 600;
        font-size: 16px;
    }

    @keyframes shimmer {
        0% {
            background-position: 200% 0;
        }
        100% {
            background-position: -200% 0;
        }
    }
    .button-name-display{
      text-align: end;
      position: relative;
      bottom:8px;
      background: #d1d2d31a;
      width:fit-content;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 9px;
      inset-inline-end: 0px;
      padding:10px;
      color:$secondary-text-color;
    }

    .highlight-background{
      background-color: #3e3e3e;
    }
</style>